import { Button } from '@progress/kendo-react-buttons'
import { DateRangePicker } from '@progress/kendo-react-dateinputs'
import over from 'lodash/fp/over'
import pipe from 'lodash/fp/pipe'
import isNil from 'lodash/isNil'
import moment from 'moment'
import * as React from 'react'
import { Editor } from 'react-draft-wysiwyg'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import {connect} from 'react-redux'
import { Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import * as App from '../../../app'
import * as RasaChromePicker from '../../../components/chrome-picker'
import * as Form from '../../../components/form'
import { Loading } from '../../../components/loading'
import * as RasaInput from '../../../components/rasa-input'
import { ApplicationState } from '../../../rootReducer'
import * as Utils from '../../../utils'
import * as CommunityConfig from '../../community-config'
import {LeadTextDropdown} from '../custom-toolbar-options/lead-text-dropdown'
import * as Actions from './actions'
import * as Constants from './constants'
import {PartnerCode} from './partner-code'
import './styles.css'
import * as Types from './types'

interface DispatchProps {
  closeModal: any
  onSubmit: any
  onEditorStateChange: any
  postLeadText: Actions.PostLeadText
  resetForm: any
  updateDate: any
  updateField: any
  updateLeadText: any
  handleColorChange: any
  handleDropDownChange: any
}
interface Props extends DispatchProps {
  app: App.State
  communityConfig: CommunityConfig.State
  modal: Types.ModalState
  form: Types.FormState
  editor: any
  leadText: any
}
export const AddEditModalComponent: React.SFC<Props> =
  ({ app,
     communityConfig,
     modal,
     closeModal,
     editor,
     form,
     updateDate,
     updateField,
     updateLeadText,
     postLeadText,
     leadText,
     onEditorStateChange,
     handleColorChange,
     handleDropDownChange,
    }) => {
  return (
    <Modal isOpen={leadText.modal.isOpen && form.fields.type === Constants.GUI_EDITOR} className="lead-text-modal">
      <ModalHeader toggle={closeModal}>
      {form.fields.id ? 'Edit Lead Text' : 'Add Lead Text'}
      </ModalHeader>
      <ModalBody>
        <PartnerCode partnerCodes={communityConfig.partnerCodes}
                      form={form}
                      updateField={updateField} />
        <div className="field field-wrapper">
          <Label className="field-label">Date Range</Label>
          <DateRangePicker
            value={{
              end: isNil(form.fields[Constants.FieldNames.EndDate]) ? null :
                form.fields[Constants.FieldNames.EndDate].toDate(),
              start: isNil(form.fields[Constants.FieldNames.StartDate]) ? null :
                form.fields[Constants.FieldNames.StartDate].toDate(),
            }}
            onChange={updateDate} />
        </div>
        <div className="field editor-wrapper">
        <Label className="field-label">Edit Area</Label>
          <Editor editorState={editor.editorState}
                  editorStyle={editor.styleObject}
                  placeholder="Begin typing...."
                  stripPastedStyles={true}
                  toolbar={Constants.toolbarObject}
                  toolbarClassName="toolbar-wrapper"
                  toolbarCustomButtons={
                    [ <RasaChromePicker.RasaChromePicker label="Background"
                                        color={editor.styleObject.background}
                                        handleChange={(color: RasaChromePicker.Color) =>
                                          handleColorChange(Constants.BACKGROUND, color) }/>,
                      <RasaChromePicker.RasaChromePicker label="Border"
                                        color={editor.border.color}
                                        handleChange={(color: RasaChromePicker.Color) =>
                                          handleColorChange(Constants.BORDER, color)} />,
                      <LeadTextDropdown label="Border Size"
                                        dataArray={Constants.borderSizes}
                                        value={editor.border.size}
                                        handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                          handleDropDownChange(Constants.BORDER_SIZE, e.target.value)} />,
                      <LeadTextDropdown label="Padding Size"
                                        dataArray={Constants.paddingSizes}
                                        value={editor.styleObject.padding}
                                        handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                          handleDropDownChange(Constants.PADDING_SIZE, e.target.value)} /> ] }
                  onEditorStateChange={onEditorStateChange} />
        </div>
        <RasaInput.RasaCollapse label="Preview Area" labelstyle="preview-label">
          <div className="preview-container">
            <div style={editor.styleObject}>
              <div className="lead-text-preview"
                dangerouslySetInnerHTML={{ __html: (editor.selectedLeadText || {}).description}} />
            </div>
          </div>
        </RasaInput.RasaCollapse>
      </ModalBody>
      <ModalFooter>
        {form.isSaving && <Loading size={Loading.Sizes.SMALL} />}
        <Button onClick={() => {
                  // Not ideal, handling dates seperately from rest of fields
                  const payload = editor.selectedLeadText
                  payload[Constants.FieldNames.CommunityPartnerCode] = form.fields.community_partner_code,
                  payload[Constants.END_DATE] = form.fields.end_date
                  payload[Constants.START_DATE] = form.fields.start_date
                  return editor.selectedLeadText.id ?
                    updateLeadText(app.selectedCommunity, payload) :
                    postLeadText(app.selectedCommunity, payload)
                }}
                primary
                disabled={isNil(form.fields.end_date) || isNil(form.fields.start_date)}
                look="outline">
          Save
        </Button>
        <Button look="outline" onClick={closeModal}>Cancel</Button>
      </ModalFooter>
    </Modal>
  )
}

export const AddEditModal = connect(
  ({app, communityConfig, leadText}: ApplicationState) =>
  ({app, communityConfig, leadText, modal: leadText.modal, form: leadText.form, editor: leadText.editor}),
  {
    closeModal: Actions.closeModal,
    handleColorChange: Actions.handleColorChange,
    handleDropDownChange: Actions.handleDropDownChange,
    onEditorStateChange: Actions.updateEditorState,
    onSubmit: Actions.saveForm,
    postLeadText: Actions.postLeadText,
    resetForm: Actions.FormActions[Form.RESET],
    updateField: Actions.updateField,
    updateLeadText: Actions.updateLeadText,
  },
  (stateProps, dispatchProps) => ({
    ...stateProps,
    ...dispatchProps,
    closeModal: over([
      () => dispatchProps.closeModal(),
      () => dispatchProps.resetForm(),
    ]),
    updateDate: pipe(
      Utils.selectValue,
      over([
        (date) => dispatchProps.updateField({
          name: Constants.FieldNames.StartDate,
          value: moment(date.start),
        }),
        (date) => dispatchProps.updateField({
          name: Constants.FieldNames.EndDate,
          value: date.end ? moment(date.end) : null,
        }),
      ]),
    ),
  }),
)(AddEditModalComponent)



// WEBPACK FOOTER //
// ./src/pages/lead-text/add-edit-modal/component.tsx